import { useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { exportToExcel, getMonthlyReportFileName } from '../utils/excelExport';
import { Download, Plus, Calendar, Clock, MapPin, Briefcase, Loader2, ChevronDown, ChevronRight, Lock, Unlock, CheckCircle, Paperclip, FileText, Filter, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatDuration, formatDecimalHours, consolidateProjects, calculateProjectTotals, getEntryProjectMinutes, getEntryWorkMinutes } from '../utils/timeCalculator';
import type { TimeEntry } from '../types';
import { PositionBreakdown } from './PositionBreakdown';

interface Props {
  onNew: () => void;
  onEdit: (id: string) => void;
}

export function Dashboard({ onNew, onEdit }: Props) {
  const { entries, projects, locations, deleteEntry, loadingData, monthlyReports, completeMonth, monthAttachments, uploadMonthAttachment, userProfile } = useData();
  const { user } = useAuth();
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set([format(new Date(), 'yyyy-MM')]));
  const [uploadingMonth, setUploadingMonth] = useState<string | null>(null);
  const [selectedProjectByMonth, setSelectedProjectByMonth] = useState<Record<string, string | null>>({});

  const handleSelectProject = (month: string, projectId: string | null) => {
    setSelectedProjectByMonth(prev => ({
      ...prev,
      [month]: projectId
    }));
  };

  const toggleMonth = (month: string) => {
    const newSet = new Set(expandedMonths);
    if (newSet.has(month)) {
      newSet.delete(month);
    } else {
      newSet.add(month);
    }
    setExpandedMonths(newSet);
  };

  const [exportingMonth, setExportingMonth] = useState<string | null>(null);

  const handleExportMonth = async (monthString: string, monthEntries: TimeEntry[]) => {
    const empNo = userProfile?.employeeNumber;
    const initials = userProfile?.initials;
    const isApproved = monthlyReports.some(r => r.month === monthString && r.reviewed && (!r.userId || r.userId === user?.id));
    const defaultVer = isApproved ? 'v1.0' : 'v0.1';
    const fileName = getMonthlyReportFileName(monthString, empNo, initials, defaultVer);
    setExportingMonth(monthString);
    try {
      await exportToExcel(
        monthEntries,
        projects,
        locations,
        monthString,
        fileName,
        true,
        {
          uploadCopy: true,
          employeeNumber: empNo,
          initials,
          userProfile,
          isApproval: isApproved
        }
      );
    } finally {
      setExportingMonth(null);
    }
  };

  const handleToggleComplete = async (month: string, completed: boolean) => {
    try {
      await completeMonth(month, completed);
      if (completed) {
        const monthEntries = entries.filter(e => (!e.userId || e.userId === user?.id) && e.date.startsWith(month));
        if (monthEntries.length > 0) {
          const empNo = userProfile?.employeeNumber;
          const initials = userProfile?.initials;
          const isApproved = monthlyReports.some(r => r.month === month && r.reviewed && (!r.userId || r.userId === user?.id));
          const defaultVer = isApproved ? 'v1.0' : 'v0.1';
          const fileName = getMonthlyReportFileName(month, empNo, initials, defaultVer);
          await exportToExcel(
            monthEntries,
            projects,
            locations,
            month,
            fileName,
            false,
            {
              uploadCopy: true,
              employeeNumber: empNo,
              initials,
              userProfile,
              isApproval: isApproved
            }
          );
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update month status';
      alert(msg);
    }
  };

  const handleFileUpload = async (month: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingMonth(month);
    try {
      await uploadMonthAttachment(month, file);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to upload attachment';
      alert(msg);
    } finally {
      setUploadingMonth(null);
      event.target.value = '';
    }
  };

  const getMonthReportStatus = (month: string) => {
    return monthlyReports.find(r => r.month === month && r.userId === user?.id);
  };

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this entry?")) {
      await deleteEntry(id);
    }
  };

  if (loadingData) {
    return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;
  }

  const getProjectName = (id: string) => projects.find(p => p.id === id)?.name || 'Unknown';
  const getLocationName = (id: string) => locations.find(l => l.id === id)?.name || 'Unknown';

  // Sort entries descending, filtered to current user
  const userEntries = user?.id ? entries.filter(e => !e.userId || e.userId === user.id) : entries;
  const sortedEntries = [...userEntries].sort((a, b) => b.date.localeCompare(a.date));

  // Group entries by month
  const groupedEntries = sortedEntries.reduce((acc, entry) => {
    const month = entry.date.slice(0, 7); // YYYY-MM
    if (!acc[month]) acc[month] = { entries: [], totalMins: 0 };
    acc[month].entries.push(entry);
    acc[month].totalMins += Math.max(0, getEntryWorkMinutes(entry));
    return acc;
  }, {} as Record<string, { entries: TimeEntry[], totalMins: number }>);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1">Manage your time entries and generate reports.</p>
        </div>
        <div className="flex gap-3">

          <button
            onClick={onNew}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-200"
          >
            <Plus className="w-4 h-4" />
            New Entry
          </button>
        </div>
      </div>

      {sortedEntries.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
          <div className="bg-indigo-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-indigo-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No time entries yet</h3>
          <p className="text-slate-500 max-w-sm mx-auto mb-6">Get started by creating your first daily time entry to begin tracking your projects.</p>
          <button
            onClick={onNew}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Entry
          </button>
        </div>
      ) : (
        <div className="space-y-10">
          {Object.entries(groupedEntries).map(([month, data]) => {
            const isExpanded = expandedMonths.has(month);
            const report = getMonthReportStatus(month);
            return (
              <div key={month} className="space-y-4">
                <div
                  className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-200 pb-3 px-1 gap-3 cursor-pointer group select-none"
                  onClick={() => toggleMonth(month)}
                >
                  <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 group-hover:text-indigo-600 transition-colors">
                    {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400 group-hover:text-indigo-500" /> : <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-500" />}
                    <Calendar className="w-5 h-5 text-indigo-500" />
                    {format(parseISO(month + '-01'), 'MMMM yyyy')}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3" onClick={e => e.stopPropagation()}>
                    {/* Information Group: Total & Status */}
                    <div className="flex items-center gap-2">
                      <div className="h-8 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50/80 px-3 rounded-full border border-indigo-150/80 shadow-2xs">
                        <Clock className="w-3.5 h-3.5 text-indigo-500" />
                        <span>Total: {formatDuration(data.totalMins)} ({(data.totalMins / 60 / 8.4).toFixed(1)}d)</span>
                      </div>

                      {(() => {
                        if (report?.reviewed) {
                          return (
                            <span className="h-8 inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 px-3 rounded-full border border-emerald-200 shadow-2xs animate-in fade-in duration-200">
                              <Lock className="w-3.5 h-3.5 shrink-0 text-emerald-500" />
                              Reviewed & Locked
                            </span>
                          );
                        }
                        if (report?.completed) {
                          return (
                            <span className="h-8 inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-50 text-amber-700 px-3 rounded-full border border-amber-200 shadow-2xs animate-in fade-in duration-200">
                              <Clock className="w-3.5 h-3.5 shrink-0 text-amber-500 animate-pulse" />
                              Awaiting Review
                            </span>
                          );
                        }
                        return (
                          <span className="h-8 inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-100 text-slate-600 px-3 rounded-full border border-slate-200 shadow-2xs animate-in fade-in duration-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            Open
                          </span>
                        );
                      })()}
                    </div>

                    {/* Visual divider between Information and Actions */}
                    <div className="hidden sm:block h-5 w-px bg-slate-200" aria-hidden="true" />

                    {/* Actions Group */}
                    <div className="flex items-center gap-2">
                      {/* Uploaded customer attachments */}
                      {(monthAttachments || [])
                        .filter(a => a.month === month && (!a.userId || a.userId === user?.id))
                        .map(att => (
                          <a
                            key={att.id}
                            href={att.onedriveWebUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={att.fileName}
                            aria-label={att.fileName}
                            className="h-8 inline-flex items-center justify-center px-2.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors shadow-2xs"
                            onClick={(e) => {
                              if (!att.onedriveWebUrl) {
                                e.preventDefault();
                                alert(`Attachment: ${att.fileName}`);
                              }
                            }}
                          >
                            <FileText className="w-3.5 h-3.5 text-emerald-600" />
                          </a>
                        ))}

                      {/* Attach Button */}
                      {!report?.reviewed && (
                        <label
                          title="Upload customer attachment for this month"
                          className={`h-8 inline-flex items-center gap-1.5 text-xs font-medium px-3 rounded-lg border transition-colors shadow-2xs cursor-pointer ${uploadingMonth === month
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : 'text-slate-700 bg-white border-slate-200 hover:bg-slate-50 hover:text-indigo-600 hover:border-slate-300'
                            }`}
                        >
                          {uploadingMonth === month ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
                          ) : (
                            <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                          )}
                          <span>{uploadingMonth === month ? 'Uploading...' : 'Attach'}</span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx"
                            disabled={uploadingMonth === month}
                            onChange={(e) => handleFileUpload(month, e)}
                          />
                        </label>
                      )}

                      {/* Export Button */}
                      <button
                        type="button"
                        onClick={() => handleExportMonth(month, data.entries)}
                        disabled={exportingMonth === month}
                        title="Export to Excel"
                        className="h-8 inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:text-indigo-600 hover:border-slate-300 px-3 rounded-lg transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
                      >
                        {exportingMonth === month ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
                        ) : (
                          <Download className="w-3.5 h-3.5 text-slate-500" />
                        )}
                        Export
                      </button>

                      {/* Complete / Reopen Month Button */}
                      {!report?.reviewed && (
                        report?.completed ? (
                          <button
                            type="button"
                            onClick={() => handleToggleComplete(month, false)}
                            className="h-8 inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 hover:text-rose-800 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-3 rounded-lg transition-colors shadow-2xs cursor-pointer"
                          >
                            <Unlock className="w-3.5 h-3.5 text-rose-500" />
                            Reopen Month
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleToggleComplete(month, true)}
                            className="h-8 inline-flex items-center gap-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-3 rounded-lg transition-all shadow-sm shadow-indigo-100 hover:shadow cursor-pointer"
                          >
                            <CheckCircle className="w-3.5 h-3.5 text-indigo-200" />
                            Complete Month
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                    {(() => {
                      const projectTotals = calculateProjectTotals(data.entries, getProjectName);
                      const selectedProjectId = selectedProjectByMonth[month] ?? null;
                      const selectedProjectInfo = selectedProjectId ? projectTotals.find(p => p.projectId === selectedProjectId) : null;
                      const displayedEntries = selectedProjectId
                        ? data.entries.filter(e => getEntryProjectMinutes(e, selectedProjectId) > 0)
                        : data.entries;

                      return (
                        <>
                          {projectTotals.length > 0 && (
                            <PositionBreakdown
                              month={month}
                              items={projectTotals}
                              totalMinutes={data.totalMins}
                              selectedProjectId={selectedProjectId}
                              onSelectProject={(id) => handleSelectProject(month, id)}
                              titlePrefix="Position"
                            />
                          )}

                          {selectedProjectInfo && (
                            <div className="flex items-center justify-between bg-indigo-50/60 border border-indigo-100 rounded-xl px-4 py-2.5">
                              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700">
                                <Filter className="w-3.5 h-3.5 text-indigo-500" />
                                Showing: <strong className="font-bold">{selectedProjectInfo.name}</strong> ({formatDuration(selectedProjectInfo.minutes)})
                              </span>
                              <button
                                type="button"
                                onClick={() => handleSelectProject(month, null)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 bg-white hover:bg-slate-100 px-2 py-1 rounded-lg transition-colors border border-slate-200 cursor-pointer shadow-2xs"
                                title="Clear position filter"
                              >
                                <X className="w-3.5 h-3.5" />
                                Show All
                              </button>
                            </div>
                          )}

                          {displayedEntries.length === 0 ? (
                            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm flex flex-col items-center justify-center text-slate-400 italic text-sm gap-2">
                              <p>No entries found for {selectedProjectInfo?.name || 'this position'} in {format(parseISO(month + '-01'), 'MMMM yyyy')}.</p>
                              <button
                                type="button"
                                onClick={() => handleSelectProject(month, null)}
                                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline not-italic cursor-pointer"
                              >
                                Show all entries
                              </button>
                            </div>
                          ) : (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                              <ul className="divide-y divide-slate-100">
                                {displayedEntries.map((entry) => {
                                  const projMins = selectedProjectId ? getEntryProjectMinutes(entry, selectedProjectId) : 0;
                                  return (
                                    <li key={entry.id} className="p-6 hover:bg-slate-50 transition-colors group">
                                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="space-y-3 flex-1">
                                          <div className="flex flex-wrap items-center gap-3">
                                            <div className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-md text-sm font-semibold flex items-center gap-1.5">
                                              {format(parseISO(entry.date), 'EEE, MMM d')}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100 px-3 py-1 rounded-md">
                                              <Clock className="w-4 h-4 text-slate-400" />
                                              {entry.timeArrived} - {entry.timeLeft}
                                              <span className="text-slate-400 font-normal">({entry.breakDurationMinutes}m break)</span>
                                              {(() => {
                                                const totalWorkMins = getEntryWorkMinutes(entry);
                                                return (
                                                  <span className="ml-1 font-bold text-indigo-600 border-l border-slate-300 pl-2 inline-flex items-center gap-1">
                                                    <span>{formatDecimalHours(totalWorkMins)}</span>
                                                    <span className="text-xs font-normal text-indigo-500/80">({formatDuration(totalWorkMins)})</span>
                                                  </span>
                                                );
                                              })()}
                                            </div>
                                            {selectedProjectId && projMins > 0 && (
                                              <span className="bg-indigo-50 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded border border-indigo-100">
                                                On Position: {formatDecimalHours(projMins)} ({formatDuration(projMins)})
                                              </span>
                                            )}
                                          </div>

                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                            <div className="flex items-start gap-2 text-sm text-slate-600">
                                              <Briefcase className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                                              <div className="space-y-1.5 flex-1 pr-4">
                                                {consolidateProjects(entry.projects).map((p, idx) => {
                                                  const isFilteredMatch = selectedProjectId && p.projectId === selectedProjectId;
                                                  return (
                                                    <div
                                                      key={`${p.projectId}-${idx}`}
                                                      className={`flex flex-col gap-1 mb-2 last:mb-0 transition-colors ${
                                                        selectedProjectId
                                                          ? isFilteredMatch
                                                            ? 'p-2 rounded-lg bg-indigo-50/70 border border-indigo-200'
                                                            : 'p-1 rounded-lg bg-slate-50/40 border border-slate-100 opacity-60'
                                                          : ''
                                                      }`}
                                                    >
                                                      <div className="flex justify-between items-center gap-4">
                                                        <span className={`font-medium truncate ${isFilteredMatch ? 'text-indigo-900 font-bold' : 'text-slate-700'}`}>
                                                          {getProjectName(p.projectId)}
                                                        </span>
                                                        <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs font-semibold inline-flex items-center gap-1">
                                                          <span>{formatDecimalHours(p.minutes || 0)}</span>
                                                          <span className="text-[11px] font-normal text-slate-400">({formatDuration(p.minutes || 0)})</span>
                                                        </span>
                                                      </div>
                                                      {p.description && (
                                                        <span className="text-slate-500 text-xs pl-2 border-l-2 border-slate-200 mt-0.5">{p.description}</span>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                            <div className="flex items-start gap-2 text-sm text-slate-600">
                                              <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                                              <span>{getLocationName(entry.locationId)}</span>
                                            </div>
                                          </div>
                                        </div>

                                        {report?.completed || report?.reviewed ? (
                                          <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-slate-100 px-2.5 py-1.5 rounded-md border border-slate-200">
                                            <Lock className="w-3.5 h-3.5 text-slate-400" />
                                            Locked
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity mt-2 md:mt-0">
                                            <button
                                              onClick={() => onEdit(entry.id)}
                                              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-md transition-colors"
                                            >
                                              Edit
                                            </button>
                                            <button
                                              onClick={() => handleDelete(entry.id)}
                                              className="text-sm font-medium text-rose-600 hover:text-rose-800 bg-rose-50 hover:bg-rose-100 px-3 py-1.5 rounded-md transition-colors"
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  );
}
